import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Crown, Clock, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUsageStats } from '@/hooks/useUsageStats';
import { formatResetTime } from '@/lib/utils';
import { FREE_QUESTIONS_PER_CATALOG } from '@/services/rateLimiter';

interface UsageIndicatorProps {
  catalogId: string;
  compact?: boolean;
}

export default function UsageIndicator({
  catalogId,
  compact = false,
}: UsageIndicatorProps) {
  const { t } = useTranslation();
  const { usageStats, loading, isAuthenticated, hasProAccess } =
    useUsageStats(catalogId);

  if (!isAuthenticated || loading) {
    return null;
  }

  if (hasProAccess) {
    return (
      <Card className={compact ? 'p-2' : 'p-4'}>
        <CardContent className={compact ? 'p-0' : 'p-0'}>
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-yellow-500" />
            <Badge
              variant="secondary"
              className="bg-yellow-100 text-yellow-800"
            >
              {t('usage.proPlan')}
            </Badge>
            <span
              className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground`}
            >
              {t('usage.unlimitedQuestions')}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage =
    (usageStats.questionsUsed / FREE_QUESTIONS_PER_CATALOG) * 100;
  const isNearLimit = usageStats.questionsRemaining <= 2;
  const isAtLimit = usageStats.questionsRemaining === 0;

  return (
    <Card className={compact ? 'p-2' : 'p-4'}>
      <CardContent className={compact ? 'p-0' : 'p-0'}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {isAtLimit && <AlertCircle className="h-4 w-4 text-red-500" />}
            <Badge variant={isAtLimit ? 'destructive' : 'outline'}>
              {t('usage.freePlan')}
            </Badge>
            <span
              className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground`}
            >
              {usageStats.questionsRemaining}{' '}
              {t('questionPractice.questionsRemaining')}
            </span>
          </div>

          {!compact && (
            <>
              <Progress
                value={progressPercentage}
                className={`h-2 ${isNearLimit ? 'progress-red' : ''}`}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {t('usage.questionsUsed', {
                    used: usageStats.questionsUsed,
                    total: FREE_QUESTIONS_PER_CATALOG,
                  })}
                </span>
                {usageStats.resetDate && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {t('usage.resets', {
                      time: formatResetTime(usageStats.resetDate),
                    })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
